import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, RotateCcw, BookOpen, Home, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { useProgress } from '../../context/ProgressContext';

export function CompletionScreen({ onClose }) {
  const navigate = useNavigate();
  const { resetProgress } = useProgress();

  const handleRestartCourse = () => {
    resetProgress();
    navigate('/module/introduction');
    if (onClose) onClose();
  };

  const handleViewCertificate = () => {
    navigate('/certificate');
    if (onClose) onClose();
  };

  const handleRevisitModules = () => {
    navigate('/');
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 md:p-10 shadow-2xl fade-in">
        {/* Celebration header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-secondary rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
            <Award className="w-10 h-10 text-secondary-900" />
          </div>
          <h2 className="font-heading font-extrabold text-3xl text-text mb-2">
            🎉 Congratulations!
          </h2>
          <p className="text-text-muted text-lg">
            You've completed the Digital Safety Course!
          </p>
        </div>

        {/* Message */}
        <p className="text-center text-text-muted mb-8">
          You've taken an important step in protecting yourself and your loved ones online. 
          Remember, digital safety is an ongoing practice—keep using what you've learned!
        </p>

        {/* Action buttons */}
        <div className="space-y-3">
          <Button
            onClick={handleViewCertificate}
            data-testid="completion-view-certificate"
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-xl py-6 text-lg font-bold"
          >
            <Award className="w-5 h-5 mr-2" />
            View Your Certificate
          </Button>

          <Button
            onClick={handleRevisitModules}
            data-testid="completion-revisit-modules"
            variant="outline"
            className="w-full rounded-xl py-5 font-semibold"
          >
            <Home className="w-5 h-5 mr-2" />
            Revisit Modules
          </Button>

          <Button
            onClick={handleRestartCourse}
            data-testid="completion-restart-course"
            variant="outline"
            className="w-full rounded-xl py-5 font-semibold text-text-muted"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Restart Course
          </Button>
        </div>

        {/* Resource prompts */}
        <div className="mt-8 pt-6 border-t border-stone-200">
          <p className="text-center text-sm text-text-muted mb-4">
            Want to keep learning? Check out the full workbook:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-digital-edition?variant=56047764865190"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-xl text-primary font-medium text-sm transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Digital Workbook
              <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-printed-edition?variant=56047888105638"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-xl text-primary font-medium text-sm transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Printed Workbook
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
